import React, { useEffect } from 'react';
import { Wrench, Loader2 } from 'lucide-react';
import { probeMaintenance } from '../services/maintenance';

/**
 * Full-page maintenance screen shown across the ENTIRE app (including the public
 * patient-facing form / booking / e-sign routes) while the backend has
 * MAINTENANCE_MODE=true — GCP migration §8 step 1.
 *
 * It polls the public `GET /api/maintenance-status` probe every 30s and reloads
 * the moment the flag clears, so users stuck here recover automatically without
 * a manual refresh (supports §9 rollback = maintenance OFF).
 */
const POLL_INTERVAL_MS = 30_000;

const MaintenanceScreen: React.FC = () => {
  useEffect(() => {
    const id = setInterval(async () => {
      const stillDown = await probeMaintenance();
      if (!stillDown) {
        window.location.reload();
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4"
      role="status"
      aria-live="polite"
    >
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
        <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
          <Wrench className="h-7 w-7 text-primary-600 dark:text-primary-400" aria-hidden="true" />
        </div>

        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 leading-tight">
          Scheduled maintenance
        </h1>

        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
          SiteOperations.io is briefly offline for scheduled maintenance. This page
          will refresh automatically as soon as we&rsquo;re back &mdash; no need to
          reload.
        </p>

        <div className="mt-6 inline-flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          <span>Checking status&hellip;</span>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceScreen;
